import PixelStatus from '@/Enums/Pixels/PixelStatus.enum';
import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import LoadingBackdrop from '@/components/Grid/LoadingBackdrop';
import StatusList from '@/components/Icons/StatusList';
import SearchInput from '@/components/SearchInput';
import SelectInputPlaceholder from '@/components/Select/InputPlaceholder';
import IOSSwitch from '@/components/Switch';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsAdmin, selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { setFilters } from '@/store/slices/pixelsSlice';
import { Add, FileDownloadOutlined } from '@mui/icons-material';
import { Button, Checkbox, ListItemText, MenuItem, Select, selectClasses } from '@mui/material';
import {
  GridApiPro,
  GridCsvGetRowsToExportParams,
  GridRowId,
  GridToolbarContainer,
  gridFilteredSortedRowIdsSelector,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { ChangeEvent, FC, useCallback, useState } from 'react';
import FilterPopover from './FilterPopover';

const getSelectedRowsToExport = ({ apiRef }: GridCsvGetRowsToExportParams<GridApiPro>): GridRowId[] => {
  return gridFilteredSortedRowIdsSelector(apiRef);
};

interface IPixelToolBarProps {
  setQuickFilter: (filter: string[]) => void;
  onCreateNewClick: () => void;
  onStatusChange: (status: PixelStatus[]) => void;
  status: PixelStatus[];
  active: boolean;
  loading: boolean;
}

const ToolBar: FC<IPixelToolBarProps> = ({
  setQuickFilter,
  onCreateNewClick,
  onStatusChange,
  status,
  active,
  loading,
}) => {
  const apiRef = useGridApiContext();

  const dispatch = useAppDispatch();
  const [filterValue, setFilterValue] = useState('');
  const [activeValue, setActiveValue] = useState(active);

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFilterValue(value);
      setQuickFilter(value.trim().split(' ') || []);
    },
    [setQuickFilter],
  );

  const onActiveChange = useCallback(() => {
    dispatch(setFilters({ active: !activeValue }));
    setActiveValue(!activeValue);
  }, [dispatch, activeValue]);

  const onGenerateReportClick = useCallback(() => {
    apiRef.current.exportDataAsCsv({
      //@ts-expect-error pro and community version mismatch
      getRowsToExport: getSelectedRowsToExport,
      fileName: `Pixels_${Date.now()}`,
    });
  }, [apiRef]);

  const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.PIXELS));
  const isAdmin = useAppSelector(selectIsAdmin);

  return (
    <>
      <GridToolbarContainer sx={{ padding: 0 }}>
        <div className='py-[24px] px-[30px] flex items-center justify-between relative w-full'>
          <div className='flex gap-[12px]'>
            <SearchInput placeholder='Search..' value={filterValue} onChange={onInputChange} />
            <Select
              startAdornment={<StatusList className='w-[20px] shrink-0' />}
              value={status}
              multiple
              displayEmpty
              renderValue={() => <SelectInputPlaceholder>Status</SelectInputPlaceholder>}
              sx={{
                height: '37px',
                [`& .${selectClasses.select}`]: {
                  paddingLeft: '4px',
                  paddingRight: 0,
                },
              }}
              className='xl:max-w-[25%] max-w-none'
              onChange={e => onStatusChange(e.target.value as PixelStatus[])}
            >
              {Object.values(PixelStatus).map(option => (
                <MenuItem key={option} value={option} sx={{ paddingLeft: '4px' }}>
                  <Checkbox checked={status.includes(option)} />
                  <ListItemText>{option}</ListItemText>
                </MenuItem>
              ))}
            </Select>
            {isAdmin && (
              <Button variant='outlined' onClick={onActiveChange} sx={{ height: '37px' }}>
                <span>Archive</span>
                <IOSSwitch sx={{ marginLeft: '7px' }} checked={!activeValue} />
              </Button>
            )}
            <FilterPopover />
          </div>
          <div className='flex gap-[8px]'>
            <Button variant='outlined' onClick={onGenerateReportClick} disabled={loading}>
              <FileDownloadOutlined sx={{ width: 20, height: 20, marginRight: '4px' }} />
              <span>Download Table</span>
            </Button>
            <Button variant='contained' onClick={onCreateNewClick} disabled={readOnly}>
              <Add sx={{ width: 16, height: 16, marginRight: '2px' }} />
              <span>Create New</span>
            </Button>
          </div>
        </div>
        {loading && <LoadingBackdrop />}
      </GridToolbarContainer>
    </>
  );
};

export default ToolBar;
